import { useMemo } from "react";
import { Task } from "@common/types/task";
import { FINANCIAL_TYPES_AVAILABLE_FOR_RECONCILIATION } from "@common/helpers/reconciliation";
import { useEntitiesById } from "@hooks/useEntities";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { BankReconciliation } from "@components/accounting/reconciliation/BankReconciliation";
import { singleEntityBankReconciliationView } from "@components/accounting/reconciliation/views/defaultReconciliationViews";
import { isFinancialAccountType } from "@components/settings/OrganizationCloseTemplates/closeTemplateTaskViewConfig";
import styles from "./ReconciliationTaskBody.module.scss";
interface Props {
    selectedTask: Task;
}

export const ReconciliationTaskBody = ({ selectedTask }: Props) => {
    const entitiesById = useEntitiesById({
        onlyBusiness: true,
        excludeMockEntities: true,
    });

    const entityId = selectedTask.entityIds?.[0];
    const entity = entityId != null ? entitiesById.get(entityId) : undefined;

    const accountIdsToShow = useMemo(() => {
        const rawIds = selectedTask.view?.filters?.accountIds;
        if (!Array.isArray(rawIds)) {
            return [];
        }
        return rawIds.filter((id): id is number => typeof id === "number");
    }, [selectedTask.view]);

    const accountTypesToShow = useMemo(() => {
        // Pinned accounts (client-specific templates) take precedence over
        // the type filter — same mutual exclusivity enforced when authoring
        // the template. When accounts are pinned, `accountIdsToShow` alone
        // narrows the table, so the type filter is left at the full list.
        const rawTypes = selectedTask.view?.filters?.financialAccountTypes;
        if (!Array.isArray(rawTypes) || rawTypes.length === 0) {
            return FINANCIAL_TYPES_AVAILABLE_FOR_RECONCILIATION;
        }
        const validated = rawTypes.filter(isFinancialAccountType);
        return validated.length > 0
            ? validated
            : FINANCIAL_TYPES_AVAILABLE_FOR_RECONCILIATION;
    }, [selectedTask.view]);

    if (!entity) {
        return <EmptyState header="No entity linked to this task" />;
    }

    return (
        <BankReconciliation
            entityIds={[entity.id]}
            view={singleEntityBankReconciliationView}
            financialAccountTypeFilter={accountTypesToShow}
            financialAccountIdFilter={accountIdsToShow}
            bankReconciliationTableClassName={styles.bankReconciliationTable}
            // The app header hides via a `body` class when fullscreen, which
            // only shifts the table's position, not its own size — the auto
            // top-offset measurement's ResizeObserver alone wouldn't catch it,
            // so force a re-measure on the flag itself.
        />
    );
};
